import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api'
import { useAuthStore } from '@/features/account/session'
import { normalizeImageFocus } from '@/lib/media/imageAssetFields'
import type { PersonaCreateData, PersonaUpdateData } from '@/api/contract'
import type { Persona } from '@/types/domain'

export const usePersonasStore = defineStore('personas', () => {
  const auth = useAuthStore()
  const accountId = ref<string | null>(null)
  const personas = ref<Persona[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastFetched = ref<number | null>(null)
  const cacheDuration = 5 * 60 * 1000
  let generation = 0
  let catalogVersion = 0
  let pendingLoad: Promise<Persona[]> | null = null

  const defaultPersona = computed(() => personas.value.find(persona => persona.is_default) ?? null)
  const fallbackPersona = computed(() => defaultPersona.value ?? personas.value[0] ?? null)

  function getPersonaById(id: number | string): Persona | undefined {
    return personas.value.find(persona => persona.id === Number(id))
  }

  function invalidateCatalog() {
    catalogVersion++
    pendingLoad = null
    lastFetched.value = null
    loading.value = false
  }

  function setAccount(id: string | null) {
    if (accountId.value === id) return
    accountId.value = id
    generation++
    invalidateCatalog()
    personas.value = []
    error.value = null
  }

  function isCurrent(accountGeneration: number) {
    return generation === accountGeneration && accountId.value === auth.userId
  }

  function beginMutation() {
    setAccount(auth.userId)
    if (!accountId.value) throw new Error('Sign in to manage personas.')
    invalidateCatalog()
    error.value = null
    return generation
  }

  function assertCurrent(accountGeneration: number) {
    if (!isCurrent(accountGeneration)) throw new Error('The active account changed. Reopen personas.')
  }

  function fetchPersonas(forceRefresh = false): Promise<Persona[]> {
    setAccount(auth.userId)
    if (!accountId.value) return Promise.resolve([])
    if (!forceRefresh && pendingLoad) return pendingLoad
    if (!forceRefresh && lastFetched.value !== null && Date.now() - lastFetched.value < cacheDuration) {
      return Promise.resolve(personas.value)
    }

    const accountGeneration = generation
    const request = ++catalogVersion
    loading.value = true
    error.value = null
    const load = (async () => {
      try {
        const response = await api.getPersonas()
        if (!isCurrent(accountGeneration) || request !== catalogVersion) return []
        personas.value = response.personas.filter(persona => persona.user_id === accountId.value)
        lastFetched.value = Date.now()
        return personas.value
      } catch (cause) {
        if (!isCurrent(accountGeneration) || request !== catalogVersion) return []
        error.value = cause instanceof Error ? cause.message : 'Failed to load personas.'
        throw cause
      } finally {
        if (isCurrent(accountGeneration) && request === catalogVersion) {
          loading.value = false
          pendingLoad = null
        }
      }
    })()
    pendingLoad = load
    return load
  }

  // A default update can partially succeed because the existing API uses two writes.
  async function reconcileFailure(accountGeneration: number) {
    assertCurrent(accountGeneration)
    invalidateCatalog()
    await fetchPersonas(true).catch(() => {})
    assertCurrent(accountGeneration)
  }

  async function createPersona(data: PersonaCreateData) {
    const accountGeneration = beginMutation()
    try {
      const response = await api.createPersona(data)
      assertCurrent(accountGeneration)
      invalidateCatalog()
      const persona = response.persona
      if (persona && persona.user_id === accountId.value) {
        if (persona.is_default) personas.value = personas.value.map(item => ({ ...item, is_default: false }))
        personas.value = [...personas.value.filter(item => item.id !== persona.id), persona]
      } else {
        await fetchPersonas(true)
        assertCurrent(accountGeneration)
      }
    } catch (cause) {
      if (data.is_default && isCurrent(accountGeneration)) await reconcileFailure(accountGeneration)
      throw cause
    }
  }

  async function updatePersona(id: number, data: PersonaUpdateData) {
    const accountGeneration = beginMutation()
    try {
      await api.updatePersona(id, data)
      assertCurrent(accountGeneration)
      invalidateCatalog()
      const { img_asset, img_focus, ...fields } = data
      const patch: Partial<Persona> = { ...fields }
      if (img_asset !== undefined) patch.imgpath = img_asset?.public_url ?? ''
      if (img_focus !== undefined) patch.img_focus = normalizeImageFocus(img_focus)
      personas.value = personas.value.map(persona => ({
        ...persona,
        ...(data.is_default ? { is_default: false } : {}),
        ...(persona.id === id ? patch : {}),
      }))
    } catch (cause) {
      if (data.is_default && isCurrent(accountGeneration)) await reconcileFailure(accountGeneration)
      throw cause
    }
  }

  async function setDefaultPersona(id: number) {
    const accountGeneration = beginMutation()
    if (!getPersonaById(id)) throw new Error('This persona is no longer available. Reload personas.')
    try {
      await api.setDefaultPersona(id)
      assertCurrent(accountGeneration)
      invalidateCatalog()
      personas.value = personas.value.map(persona => ({ ...persona, is_default: persona.id === id }))
    } catch (cause) {
      if (isCurrent(accountGeneration)) await reconcileFailure(accountGeneration)
      throw cause
    }
  }

  async function deletePersona(id: number) {
    const accountGeneration = beginMutation()
    const replaceDefault = fallbackPersona.value?.id === id
    await api.deletePersona(id)
    assertCurrent(accountGeneration)
    invalidateCatalog()
    personas.value = personas.value.filter(persona => persona.id !== id)
    // Preserve the existing promotion rule without changing any story's selection.
    const replacement = fallbackPersona.value
    if (replaceDefault && replacement && !replacement.is_default) {
      await setDefaultPersona(replacement.id)
      assertCurrent(accountGeneration)
    }
  }

  return {
    accountId, personas, loading, error, lastFetched,
    defaultPersona, fallbackPersona, getPersonaById,
    setAccount, fetchPersonas, createPersona, updatePersona, deletePersona, setDefaultPersona,
  }
})
